import React from "react";
import { Reveal } from "@/components/Motion";
import CtaButton from "@/components/CtaButton";
import { firstBook, getBookStage } from "@/config/site";

// Renders the correct amount of book information based on the active stage
// (resolved from the schedule in site.js). Everything is data-driven so the
// campaign can advance on its scheduled dates without a redesign.
export default function BookReveal() {
  const b = firstBook;
  const stage = getBookStage();
  const showTitle = stage >= 2 && b.title;
  const showCover = stage >= 3 && b.coverImage;
  const showPreorder = stage >= 4 && stage < 5 && b.preorderUrl;
  const showRetailers = stage >= 4 && b.retailers?.length > 0;
  const isAvailable = stage >= 5;

  // Stage 1: nothing revealed yet — handled by the Books page mystery block.
  if (stage < 2) return null;

  return (
    <section data-testid="book-reveal" className="mx-auto max-w-[1200px] px-5 sm:px-8 py-24">
      <div className="grid gap-12 md:grid-cols-12 md:items-center md:gap-16">
        {showCover && (
          <Reveal className="md:col-span-4">
            <img
              src={b.coverImage}
              alt={b.coverAlt || (b.title ? `${b.title} cover` : "Book cover")}
              className="mx-auto w-full max-w-[380px] border border-dh-cream/10 object-cover shadow-2xl"
            />
          </Reveal>
        )}
        <div className={showCover ? "md:col-span-8" : "md:col-span-12"}>
          {b.seriesName && (
            <Reveal>
              <p className="font-body text-[11px] uppercase tracking-ultra text-dh-gold">{b.seriesName}</p>
            </Reveal>
          )}
          {showTitle && (
            <Reveal delay={0.05}>
              <h2 className="mt-4 font-display text-4xl leading-tight text-dh-cream sm:text-5xl">
                {b.title}
              </h2>
            </Reveal>
          )}
          {showTitle && b.author && (
            <Reveal delay={0.08}>
              <p className="mt-3 font-serif-soft text-xl italic text-dh-cream/80">By {b.author}</p>
            </Reveal>
          )}
          {b.description && (
            <Reveal delay={0.1}>
              <p className="mt-6 max-w-xl font-body text-base leading-relaxed text-dh-cream/75">
                {b.description}
              </p>
            </Reveal>
          )}

          <Reveal delay={0.15}>
            <div className="mt-8 space-y-2">
              {b.releaseDate && (
                <p className="font-body text-sm text-dh-muted">
                  <span className="text-dh-cream/80">{isAvailable ? "Release:" : "Coming"}</span>{" "}{b.releaseDate}
                </p>
              )}
              {b.presaleDate && (
                <p className="font-body text-sm text-dh-muted">
                  <span className="text-dh-cream/80">Presale begins:</span>{" "}{b.presaleDate}
                </p>
              )}
              {b.formats?.length > 0 && (
                <p className="font-body text-sm text-dh-muted">
                  <span className="text-dh-cream/80">Formats:</span> {b.formats.join(" · ")}
                </p>
              )}
              {b.foundingReader && (
                <p className="font-body text-sm text-dh-muted">{b.foundingReader}</p>
              )}
            </div>
            <div className="gold-rule mt-8 w-16" />
          </Reveal>

          <div className="mt-10 flex flex-wrap gap-4">
            {showPreorder && (
              <CtaButton href={b.preorderUrl} variant="solid" testId="book-preorder-btn">
                Preorder Now
              </CtaButton>
            )}
            {showRetailers &&
              b.retailers.map((r) => (
                <CtaButton key={r.name} href={r.url} variant="ghost" testId={`book-retailer-${r.name}`}>
                  {isAvailable ? r.name : `Preorder · ${r.name}`}
                </CtaButton>
              ))}
          </div>

          {isAvailable && b.reviews?.length > 0 && (
            <div className="mt-14 space-y-8 border-t border-dh-cream/10 pt-10">
              {b.reviews.map((rev, i) => (
                <Reveal key={i} delay={i * 0.05}>
                  <blockquote className="max-w-2xl">
                    <p className="font-serif-soft text-2xl italic leading-snug text-dh-cream/90">
                      “{rev.quote}”
                    </p>
                    <cite className="mt-3 block font-body text-xs uppercase tracking-[0.2em] text-dh-gold not-italic">
                      {rev.source}
                    </cite>
                  </blockquote>
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
